import type { ReactNode } from 'react';
import { Alert } from './Alert';
import { Button } from './Button';
import { Modal } from './Modal';

/**
 * Confirmación de una acción destructiva (desconectar un canal, borrar un método de pago,
 * quitar a un miembro del equipo).
 *
 * Es un `Modal` con la decisión ya resuelta: mensaje claro, botón de la acción en rojo y el
 * error del backend a la vista (por ejemplo, el "no puedes eliminar al último
 * administrador" que responde la API). No se usa `window.confirm` para no salir del sistema
 * de diseño ni quedarse sin mensaje de error.
 */

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: ReactNode;
  /** Texto del botón que ejecuta la acción (ej. "Desconectar WhatsApp"). */
  confirmLabel: string;
  /** `danger` pinta el botón en rojo. */
  tone?: 'danger' | 'primary';
  /** Ocupa el botón y lo bloquea mientras la mutación está en curso. */
  loading?: boolean;
  /** Mensaje del backend si la acción falló. */
  error?: string | null;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  tone = 'danger',
  loading = false,
  error,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant={tone} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {error ? <Alert>{error}</Alert> : null}

      <p className="text-caption text-ink-muted">
        Esta acción se aplica de inmediato. Si te equivocas, tendrás que volver a configurarlo.
      </p>
    </Modal>
  );
}
